import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import PageHero3D from '../fx/PageHero3D';
import Seo from '../seo/Seo';
import LeadSection from '../sections/LeadSection';
import FloatingContact from '../common/FloatingContact';
import { useInteractions } from '../../hooks/useInteractions';

/* Direct content blocks inside sections / heroes. Grids (.row, *-grid, *-list)
   are not revealed themselves; their children are, with a stagger. */
const BLOCK_SELECTOR =
  'main .section .container > *, main .section-alt .container > *, main .cta-section .container > *, main .faq-section .container > *, main .page-hero .container > *, main .page-header .container > *';
const GRID_RE = /(^|\s)(row|[a-z-]*grid|[a-z-]*list|[a-z-]*cards|[a-z-]*steps)(\s|$)/;

function collectTargets() {
  const out = [];
  document.querySelectorAll(BLOCK_SELECTOR).forEach((el) => {
    const isGrid = GRID_RE.test(el.className || '') && el.children.length > 1;
    if (isGrid) {
      [...el.children].forEach((child, i) => out.push([child, Math.min(i, 6) * 70]));
    } else {
      out.push([el, 0]);
    }
  });
  return out;
}

/**
 * MainLayout — shared Header/Footer plus site-wide motion:
 * 3D page heroes, staggered scroll-reveal, card tilt, magnetic buttons,
 * cursor glow and a back-to-top button.
 */
function MainLayout() {
  const { pathname } = useLocation();
  const [showTop, setShowTop] = useState(false);
  const glowRef = useRef(null);

  useInteractions(glowRef);

  /* Scroll-reveal on every inner page (Home handles its own) */
  useEffect(() => {
    if (pathname === '/') return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;

    let pending = [];
    let raf = 0;

    const check = () => {
      raf = 0;
      const limit = window.innerHeight * 0.92;
      pending = pending.filter((el) => {
        if (document.hidden || el.getBoundingClientRect().top < limit) {
          el.classList.add('js-reveal--in');
          return false;
        }
        return true;
      });
    };
    const queue = () => {
      if (document.hidden) { check(); return; } // rAF is paused in background tabs
      if (!raf) raf = requestAnimationFrame(check);
    };

    const scan = () => {
      collectTargets().forEach(([el, delay]) => {
        if (el.dataset.revealed) return;
        el.dataset.revealed = '1';
        if (el.getBoundingClientRect().top < window.innerHeight * 0.92) return; // already in view
        el.style.transitionDelay = `${delay}ms`;
        el.classList.add('js-reveal');
        pending.push(el);
      });
      queue();
    };
    // lazy route chunks may mount after the first pass, so scan a few times
    const timers = [60, 400, 1000, 2000].map((ms) => setTimeout(scan, ms));
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);

    return () => {
      timers.forEach(clearTimeout);
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', queue);
      window.removeEventListener('resize', queue);
      document.querySelectorAll('[data-revealed]').forEach((el) => {
        el.classList.remove('js-reveal', 'js-reveal--in');
        el.style.transitionDelay = '';
        delete el.dataset.revealed;
      });
    };
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 700);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="site-wrapper">
      <div className="cursor-glow" ref={glowRef} aria-hidden="true" />
      <Seo pathname={pathname} />
      <Header />
      <main className="site-main" id="main-content" tabIndex="-1">
        <Outlet />
      </main>
      {!pathname.startsWith('/contact') && <LeadSection />}
      <Footer />
      <FloatingContact />
      <PageHero3D pathname={pathname} />
      <button
        type="button"
        className={`to-top${showTop ? ' is-on' : ''}`}
        aria-label="Back to top"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 19V5M5 12l7-7 7 7" /></svg>
      </button>
    </div>
  );
}

export default MainLayout;
